/* CODEX Live Chat — uses a WebSocket to /api/codex/chat/ws/{peerId}?token=...
   Falls back to REST send if WS fails.
*/
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ChevronLeft, MessageCircle, Mic, Headphones } from 'lucide-react';
import { codexApi } from '../api';
import VoiceRecorder from '../components/VoiceRecorder';
import VoiceNotePlayer from '../components/VoiceNotePlayer';

const BASE = process.env.REACT_APP_BACKEND_URL;

function buildWsUrl(peerId) {
  const u = new URL(BASE);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = `/api/codex/chat/ws/${peerId}`;
  const t = localStorage.getItem('access_token');
  if (t) u.searchParams.set('token', t);
  return u.toString();
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function ThreadList({ threads, activeId, onPick, myId }) {
  if (!threads.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'rgba(253,246,227,0.6)', fontSize: 13 }}>
        <MessageCircle size={28} style={{ opacity: 0.6, marginBottom: 6 }} />
        <div>No conversations yet.</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {threads.map((t) => {
        const isActive = activeId === t.peer_id;
        const fromMe = t.last_message?.sender_id === myId;
        return (
          <Link
            key={t.peer_id}
            to={`/codex/chat/${t.peer_id}`}
            data-testid={`chat-thread-${t.peer_id}`}
            style={{
              padding: 12,
              borderRadius: 10,
              textDecoration: 'none',
              color: 'inherit',
              background: isActive ? 'rgba(212,175,55,0.16)' : 'rgba(245,217,124,0.04)',
              border: `1px solid ${isActive ? 'rgba(212,175,55,0.4)' : 'rgba(245,217,124,0.1)'}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <span className="codex-display" style={{ color: 'var(--codex-gold-light)', fontSize: 14 }}>{t.peer_name}</span>
              {t.unread > 0 && (
                <span style={{
                  background: 'var(--codex-orange)', color: '#1a0e2c',
                  fontSize: 10, padding: '2px 7px', borderRadius: 999, fontFamily: "'Cinzel', serif",
                }}>{t.unread}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(253,246,227,0.7)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {fromMe ? 'You: ' : ''}{t.last_message?.body || ''}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(245,217,124,0.5)', marginTop: 2 }}>
              {fmtTime(t.last_message?.created_at)}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function MessageBubble({ msg, mine }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`chat-message-${msg.id}`}
      style={{
        alignSelf: mine ? 'flex-end' : 'flex-start',
        maxWidth: '70%',
        padding: '9px 14px',
        borderRadius: 14,
        background: mine
          ? 'linear-gradient(135deg, rgba(212,175,55,0.22), rgba(234,88,12,0.12))'
          : 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(11,10,22,0.4))',
        border: `1px solid ${mine ? 'rgba(212,175,55,0.35)' : 'rgba(168,85,247,0.35)'}`,
        color: 'var(--codex-parchment)',
        whiteSpace: 'pre-wrap',
        fontSize: 14,
        lineHeight: 1.45,
      }}
    >
      {!mine && (
        <div style={{ fontSize: 10, color: '#c4b5fd', letterSpacing: '0.1em', marginBottom: 4, fontFamily: "'Cinzel', serif" }}>
          {(msg.sender_name || '').toUpperCase()}
        </div>
      )}
      {msg.body}
      <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4, textAlign: mine ? 'right' : 'left' }}>
        {fmtTime(msg.created_at)}
      </div>
    </motion.div>
  );
}

export default function CodexChat() {
  const { peerId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [voiceThread, setVoiceThread] = useState([]);
  const [draft, setDraft] = useState('');
  const [peer, setPeer] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [showVoice, setShowVoice] = useState(false);
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);

  const myId = me?.user?.id;

  const loadThreads = async () => {
    try {
      const t = await codexApi.chatThreads();
      setThreads(t);
    } catch { /* swallow */ }
  };

  // Identify current user (use auth/me — not on codex api wrapper)
  useEffect(() => {
    fetch(`${BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((u) => setMe(u ? { user: u } : null));
    loadThreads();
  }, []);

  // Connect WS when peer changes
  useEffect(() => {
    if (!peerId) return undefined;

    let cancelled = false;
    let pingInterval = null;

    // Load REST history first (works offline-from-WS)
    codexApi.chatHistory(peerId).then((d) => {
      if (!cancelled) {
        setHistory(d.messages || []);
        setPeer(d.peer);
        setTimeout(() => scrollToBottom(), 60);
      }
    }).catch((e) => setError(e.message));

    // Voice thread
    codexApi.voiceThread(peerId).then((v) => { if (!cancelled) setVoiceThread(v); }).catch(() => {});

    // Mark thread read
    codexApi.chatMarkRead(peerId).catch(() => {});

    // Open WS
    try {
      const ws = new WebSocket(buildWsUrl(peerId));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onerror = () => setError('Live connection unavailable; using fallback.');
      ws.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.type === 'history') {
            setHistory(payload.data || []);
          } else if (payload.type === 'message') {
            setHistory((prev) => {
              if (prev.find((m) => m.id === payload.data.id)) return prev;
              return [...prev, payload.data];
            });
            setTimeout(() => scrollToBottom(), 30);
            loadThreads();
          }
        } catch { /* swallow */ }
      };
      // ping every 25s to keep idle proxies alive
      pingInterval = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);
    } catch (e) {
      setError(e.message);
    }

    return () => {
      cancelled = true;
      if (pingInterval) clearInterval(pingInterval);
      try { wsRef.current?.close(); } catch { /* swallow */ }
      wsRef.current = null;
    };
    // eslint-disable-next-line
  }, [peerId]);

  const scrollToBottom = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  };

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'send', body }));
    } else {
      try {
        const m = await codexApi.chatSend(peerId, body);
        setHistory((prev) => [...prev, m]);
        loadThreads();
        setTimeout(() => scrollToBottom(), 30);
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const combined = useMemo(() => {
    // Merge text + voice messages by created_at
    const v = voiceThread.map((vn) => ({ ...vn, _kind: 'voice' }));
    const t = history.map((m) => ({ ...m, _kind: 'text' }));
    return [...t, ...v].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [history, voiceThread]);

  return (
    <div data-testid="codex-chat-page" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 18, minHeight: '70vh' }}>
      {/* Left rail: threads */}
      <aside className="codex-card" style={{ padding: 14, alignSelf: 'flex-start' }}>
        <div className="codex-eyebrow" style={{ marginBottom: 10 }}>
          <MessageCircle size={11} style={{ verticalAlign: '-1px' }} /> CONVERSATIONS
        </div>
        <ThreadList threads={threads} activeId={peerId} myId={myId} />
      </aside>

      {/* Right pane: thread */}
      <section className="codex-card" style={{ padding: 0, display: 'flex', flexDirection: 'column', minHeight: 540 }}>
        {!peerId ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, color: 'rgba(253,246,227,0.7)' }}>
            <MessageCircle size={48} color="var(--codex-gold-light)" />
            <div className="codex-display" style={{ marginTop: 12, fontSize: 22, color: 'var(--codex-gold-light)' }}>Pick a conversation</div>
            <div style={{ fontSize: 13, marginTop: 6, opacity: 0.8 }}>Select someone on the left to begin.</div>
          </div>
        ) : (
          <>
            <header style={{
              padding: '14px 18px',
              borderBottom: '1px solid rgba(245,217,124,0.15)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => navigate('/codex/chat')}
                  className="codex-btn codex-btn-ghost"
                  style={{ padding: 6, fontSize: 11 }}
                  data-testid="chat-back"
                >
                  <ChevronLeft size={14} />
                </button>
                <div>
                  <div className="codex-display" style={{ color: 'var(--codex-gold-light)', fontSize: 16 }}>{peer?.name || 'Chat'}</div>
                  <div style={{ fontSize: 11, color: connected ? '#86efac' : 'rgba(253,246,227,0.5)' }}>
                    {connected ? '● Live' : 'Offline mode'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowVoice((v) => !v)}
                className="codex-btn codex-btn-ghost"
                data-testid="chat-voice-toggle"
                style={{ padding: '6px 12px', fontSize: 11 }}
              >
                <Mic size={12} /> {showVoice ? 'Hide voice' : 'Voice note'}
              </button>
            </header>

            <div
              ref={scrollerRef}
              data-testid="chat-scroller"
              style={{
                flex: 1, overflowY: 'auto', padding: 18,
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              {combined.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, opacity: 0.65, fontSize: 13 }}>Say hello.</div>
              ) : (
                combined.map((m) => (
                  m._kind === 'voice' ? (
                    <VoiceNotePlayer key={`v-${m.id}`} note={m} side={m.sender_id === myId ? 'out' : 'in'} />
                  ) : (
                    <MessageBubble key={m.id} msg={m} mine={m.sender_id === myId} />
                  )
                ))
              )}
            </div>

            <AnimatePresence>
              {showVoice && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ borderTop: '1px solid rgba(245,217,124,0.15)', padding: 12 }}
                >
                  <VoiceRecorder
                    recipientId={peerId}
                    onSent={() => {
                      setShowVoice(false);
                      codexApi.voiceThread(peerId).then(setVoiceThread).catch(() => {});
                    }}
                    compact
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <footer style={{
              padding: 12,
              borderTop: '1px solid rgba(245,217,124,0.15)',
              display: 'flex', gap: 8,
            }}>
              <textarea
                className="codex-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder={`Message ${peer?.name || 'them'}…`}
                rows={1}
                maxLength={2000}
                data-testid="chat-input"
                style={{ flex: 1, resize: 'none', minHeight: 38, maxHeight: 120 }}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!draft.trim()}
                data-testid="chat-send"
                className="codex-btn codex-btn-primary"
                style={{ padding: '8px 14px' }}
              >
                <Send size={14} />
              </button>
            </footer>
          </>
        )}
      </section>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', top: 80, right: 20, zIndex: 50,
              padding: '8px 14px', borderRadius: 10,
              background: 'rgba(234,88,12,0.18)',
              border: '1px solid rgba(234,88,12,0.4)',
              color: '#ffb088', fontSize: 12,
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
